// The items of a purchase already bought and paid for (F-CTRL-N75): search or
// scan, quantity, price paid, lot, use-by, and photos of the bill. Shared by
// Purchasing's "Already bought" and the items of a Day Close stock cash-out,
// so both record stock the same way. Strings: boughtStrings.ts. Rules:
// marketLogic.ts.
import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Camera, Loader2, Trash2, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatMoney } from '@/lib/currency'
import { cn } from '@/lib/utils'
import { shrinkPhoto } from '@/lib/shrinkPhoto'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import { addMarketLine, type MarketDraftLine } from '@/lib/inventoryFlow/marketLogic'
import { businessTodayIso } from '@/lib/expiry'
import { useDayBoundaryHour } from '@/hooks/useDayBoundaryHour'
import { useItemScanner } from '@/components/store/inventory/useItemScanner'
import { QtyStepper } from '@/components/inventory-flow/QtyStepper'

export type BillPhoto = { content_type: string; data_base64: string }

export function BoughtItemsEditor({ lines, onLines, photos, onPhotos, onError, initialSearch = '' }: {
  lines: MarketDraftLine[]
  onLines: (update: (l: MarketDraftLine[]) => MarketDraftLine[]) => void
  photos: BillPhoto[]
  onPhotos: (update: (p: BillPhoto[]) => BillPhoto[]) => void
  onError: (message: string) => void
  /** Starts the search with this text, e.g. the Day Close reason "Milk". */
  initialSearch?: string
}) {
  const [search, setSearch] = useState(initialSearch)
  const [photoBusy, setPhotoBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const { data: itemsRes } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), staleTime: 60_000 })
  const items = useMemo(() => itemsRes?.data ?? [], [itemsRes])
  // F-CTRL-N73: tracked items get their use-by prefilled from the shelf life.
  const today = businessTodayIso(useDayBoundaryHour())
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return items.filter((i) => !i.is_prep && (i.name.toLowerCase().includes(q) || (i.code ?? '').toLowerCase().includes(q))).slice(0, 20)
  }, [items, search])
  const scanner = useItemScanner({
    enabled: true,
    onItem: (m) => {
      const known = items.find((i) => i.id === m.item.id)
      onLines((l) => addMarketLine(l, known ?? { id: m.item.id, name: m.item.name, unit: m.item.unit }, m.pack_qty || 1, today))
      return { ok: true, text: m.item.name }
    },
  })
  const set = (id: string, patch: Partial<MarketDraftLine>) =>
    onLines((l) => l.map((x) => (x.stock_item_id === id ? { ...x, ...patch } : x)).filter((x) => x.qty > 0))

  const addPhoto = async (f: File | undefined) => {
    if (!f) return
    setPhotoBusy(true)
    try {
      const p = await shrinkPhoto(f)
      onPhotos((ps) => [...ps, p].slice(0, 3))
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Could not read the photo')
    } finally {
      setPhotoBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Input placeholder={BT.search} value={search} onChange={(e) => setSearch(e.target.value)} />
        {scanner.ui}
        {search.trim() && (
          <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto">
            {matches.length === 0 && <p className="text-sm text-muted-foreground">{BT.noMatch}</p>}
            {matches.map((i) => (
              <button key={i.id} type="button" className="min-h-10 rounded-full border px-3 text-sm hover:bg-muted touch-manipulation"
                onClick={() => { onLines((l) => addMarketLine(l, i, 1, today)); setSearch('') }}>
                {i.name} <span className="text-muted-foreground">({i.unit})</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {lines.length > 0 && (
        <ul className="space-y-2">
          {lines.map((l) => (
            <li key={l.stock_item_id} className="space-y-2 rounded-lg border p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-[8rem] flex-1 font-medium">{l.name}</span>
                <QtyStepper value={l.qty} unit={l.unit} label={l.name} min={0} onChange={(v) => set(l.stock_item_id, { qty: v })} />
                <Button variant="ghost" className="h-10 w-10 p-0" aria-label={BT.remove} onClick={() => set(l.stock_item_id, { qty: 0 })}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Input type="number" min={0} step="0.01" inputMode="decimal" className="w-36" aria-label={BT.price} placeholder={BT.price}
                  value={l.unit_cost} onChange={(e) => set(l.stock_item_id, { unit_cost: e.target.value })} />
                <Input className="w-32" placeholder={BT.lot} aria-label={BT.lot} value={l.lot_no} onChange={(e) => set(l.stock_item_id, { lot_no: e.target.value })} />
                <label className="flex items-center gap-1 text-sm">
                  <span className={l.tracks_expiry && !l.expiry_date ? 'font-medium text-danger-fg' : 'text-muted-foreground'}>
                    {l.tracks_expiry ? `${BT.expiryRequired}*` : BT.expiry}
                  </span>
                  <Input type="date" className="w-44" aria-label={l.tracks_expiry ? BT.expiryRequired : BT.expiry} aria-required={l.tracks_expiry}
                    value={l.expiry_date} onChange={(e) => set(l.stock_item_id, { expiry_date: e.target.value })} />
                </label>
                <span className="ml-auto self-center font-semibold tabular-nums">{formatMoney(l.qty * (Number(l.unit_cost) || 0))}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="space-y-2">
        <span className="text-sm font-medium">{BT.photos}</span>
        <div className="flex flex-wrap items-center gap-2">
          {photos.map((p, i) => (
            <span key={i} className="relative">
              <img src={`data:${p.content_type};base64,${p.data_base64}`} alt="" className="h-10 w-10 rounded-lg object-cover" />
              <button type="button" aria-label={BT.remove} className="absolute -right-2 -top-2 rounded-full bg-background p-0.5 shadow" onClick={() => onPhotos((ps) => ps.filter((_, j) => j !== i))}><X className="h-4 w-4" /></button>
            </span>
          ))}
          {photos.length < 3 && (
            <Button type="button" variant="outline" disabled={photoBusy} onClick={() => fileRef.current?.click()}>
              {photoBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />} {photoBusy ? BT.photoBusy : BT.addPhoto}
            </Button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void addPhoto(e.target.files?.[0])} />
        </div>
      </div>
    </div>
  )
}

/** A row of big toggle buttons (one choice). */
export function ChoiceRow<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: readonly (readonly [T, string])[]; onChange: (v: T) => void
}) {
  return (
    <div className="space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, text]) => (
          <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
            className={cn('h-10 rounded-lg border-2 px-4 text-sm font-semibold touch-manipulation', value === v ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card hover:bg-muted')}>
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}
